import { useEffect } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { setWeekData } from '../redux/slices/weekDataSlice';

/**
 * Custom hook to calculate and manage week data for scheduling components
 * 
 * @param {number} numberOfWeeks - The number of weeks to calculate (default: 8)
 * @returns {Object} Object containing weekColumns and helper functions
 */
const useWeekCalculations = (numberOfWeeks = 8) => {
  const dispatch = useDispatch();
  const weekData = useSelector((state) => state.weekData);
  
  useEffect(() => {
    // Only calculate and dispatch if we don't have data or the count doesn't match
    if (!weekData.columns || weekData.columns.length !== numberOfWeeks) {
      const today = moment();
      
      // Calculate full weeks (Sunday-Saturday)
      const fullWeeks = [];
      for (let i = 0; i < numberOfWeeks; i++) {
        const weekStart = moment(today).add(i, 'weeks').startOf('week');
        const weekEnd = moment(weekStart).endOf('week');
        const isoWeekNum = weekStart.isoWeek(); // Get ISO week number of the year
        fullWeeks.push({
          weekNum: i,
          startDate: weekStart.toISOString(), // Convert to ISO string for Redux
          endDate: weekEnd.toISOString(),     // Convert to ISO string for Redux
          isoWeekNum, // Add ISO week number
          label: `Week ${i+1} (${isoWeekNum}): ${weekStart.format('MM/DD')} - ${weekEnd.format('MM/DD')}`
        });
      }
      
      // Calculate work weeks (Monday-Friday)
      const workWeeks = [];
      for (let i = 0; i < numberOfWeeks; i++) {
        const weekStart = moment(today).add(i, 'weeks').startOf('isoWeek'); // Monday
        const weekEnd = moment(weekStart).add(4, 'days'); // Friday
        const isoWeekNum = weekStart.isoWeek(); // Get ISO week number of the year
        workWeeks.push({
          weekNum: i,
          startDate: weekStart.toISOString(), // Convert to ISO string for Redux
          endDate: weekEnd.toISOString(),     // Convert to ISO string for Redux
          isoWeekNum, // Add ISO week number
          label: `Week ${i+1} (${isoWeekNum}): ${weekStart.format('MM/DD')} - ${weekEnd.format('MM/DD')}`
        });
      }
      
      dispatch(setWeekData({
        columns: fullWeeks,
        workWeekColumns: workWeeks,
        calculatedAt: today.toISOString(),
        count: numberOfWeeks
      }));
    }
  }, [dispatch, numberOfWeeks, weekData.columns]);
  
  /**
   * Determines which week column a date falls into
   * @param {string|Date} date - The date to check
   * @param {boolean} useWorkWeek - Whether to use work week (Mon-Fri) or full week (Sun-Sat)
   * @returns {number|null} - The week number or null if outside range
   */
  const getWeekForDate = (date, useWorkWeek = false) => {
    if (!date) return null;
    
    const dateToCheck = moment(date);
    
    // Get the reference date from state (either customStartDate or calculatedAt)
    const referenceDate = weekData.customStartDate 
      ? moment(weekData.customStartDate) 
      : (weekData.calculatedAt ? moment(weekData.calculatedAt) : moment());

    // Apply any week offset that was used to calculate the data
    const baseDate = moment(referenceDate);
    if (weekData.weekOffset) {
      baseDate.add(weekData.weekOffset, 'weeks');
    }
    
    // Get the columns to check against
    const columnsToCheck = useWorkWeek ? weekData.workWeekColumns : weekData.columns;
    
    // Loop through each week column and check if the date falls within it
    for (let i = 0; i < columnsToCheck.length; i++) {
      const weekStart = moment(columnsToCheck[i].startDate);
      const weekEnd = moment(columnsToCheck[i].endDate);
      
      if (dateToCheck.isSameOrAfter(weekStart, 'day') && 
          dateToCheck.isSameOrBefore(weekEnd, 'day')) {
        return i;
      }
    }
    
    // If we didn't find a match, calculate weeks difference as before for older behavior
    const weeksDiff = Math.floor(moment.duration(dateToCheck.diff(baseDate)).asWeeks());
    
    // Return the appropriate week column or null if outside our range
    return weeksDiff >= 0 && weeksDiff < weekData.count ? weeksDiff : null;
  };
  
  /**
   * Force recalculation of week data
   * @param {Date|string|moment} customStartDate - Optional custom start date
   * @param {number} weekOffset - Optional week offset from today (0 = current week, 1 = next week, etc.)
   * @param {number} newNumberOfWeeks - Override the number of weeks to calculate
   */
  const refreshWeekData = (customStartDate = null, weekOffset = 0, newNumberOfWeeks = null) => {
    // Start from today or the provided custom date
    const startDate = customStartDate ? moment(customStartDate) : moment();
    
    // Apply week offset if provided
    if (weekOffset > 0) {
      startDate.add(weekOffset, 'weeks');
    }
    
    // Calculate full weeks (Sunday-Saturday)
    const fullWeeks = [];
    const weeksToCalculate = newNumberOfWeeks || numberOfWeeks;
    for (let i = 0; i < weeksToCalculate; i++) {
      const weekStart = moment(startDate).add(i, 'weeks').startOf('week');
      const weekEnd = moment(weekStart).endOf('week');
      const isoWeekNum = weekStart.isoWeek(); // Get ISO week number of the year
      fullWeeks.push({
        weekNum: i,
        startDate: weekStart.toISOString(), // Convert to ISO string for Redux
        endDate: weekEnd.toISOString(),     // Convert to ISO string for Redux
        isoWeekNum, // Add ISO week number
        label: `Week ${i+1} (${isoWeekNum}): ${weekStart.format('MM/DD')} - ${weekEnd.format('MM/DD')}`
      });
    }
    
    // Calculate work weeks (Monday-Friday)
    const workWeeks = [];
    for (let i = 0; i < weeksToCalculate; i++) {
      const weekStart = moment(startDate).add(i, 'weeks').startOf('isoWeek'); // Monday
      const weekEnd = moment(weekStart).add(4, 'days'); // Friday
      const isoWeekNum = weekStart.isoWeek(); // Get ISO week number of the year
      workWeeks.push({
        weekNum: i,
        startDate: weekStart.toISOString(), // Convert to ISO string for Redux
        endDate: weekEnd.toISOString(),     // Convert to ISO string for Redux
        isoWeekNum, // Add ISO week number
        label: `Week ${i+1} (${isoWeekNum}): ${weekStart.format('MM/DD')} - ${weekEnd.format('MM/DD')}`
      });
    }
    
    dispatch(setWeekData({
      columns: fullWeeks,
      workWeekColumns: workWeeks,
      calculatedAt: startDate.toISOString(),
      count: weeksToCalculate,
      customStartDate: customStartDate ? moment(customStartDate).toISOString() : null,
      weekOffset: weekOffset
    }));
  };
  
  return {
    weekColumns: weekData.columns || [],
    workWeekColumns: weekData.workWeekColumns || [],
    getWeekForDate,
    refreshWeekData,
    calculatedAt: weekData.calculatedAt
  };
};

export default useWeekCalculations;